import { useState } from 'react';

// mui
import { Button, Chip, ListItem, Stack, Box, Typography, Tooltip, IconButton, Menu, MenuItem } from '@mui/material';

// icons
import {
    FavoriteBorder as FavoriteBorderIcon,
    Favorite as FavoriteIcon,
    MoreHoriz as MoreHorizIcon,
} from '@mui/icons-material';

// component
import { addNavigation, createProfileProp, requireLogin } from '../';

// api
import { Report } from '../../api';

// client-shared
import { time, number } from '@goldblack/client-utils';
import { UserAvatar, UserBadges, ReportDialog, DotLoading } from '@goldblack/client-comps';
import { storage } from '@goldblack/client-storage';

function CommentListItem(props) {
    const { user: userSession } = storage.transience.getValue();
    const [pop, setPop] = useState(null); // heart animation right after a click: 'like' | 'unlike'
    const [openReport, setOpenReport] = useState(false);
    const [reason, setReason] = useState('');
    const [reportOkDisabled, setReportOkDisabled] = useState(true);
    const [menuAnchor, setMenuAnchor] = useState(null);

    const isOwnComment = props.user?._id === userSession?._id;

    const onClickMore = (e) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
    };
    const onCloseMenu = () => setMenuAnchor(null);
    const onClickReport = () => {
        if (!requireLogin()) return;
        setMenuAnchor(null);
        setOpenReport(true);
    };
    const onReportClose = () => setOpenReport(false);
    const onReportCancel = () => setOpenReport(false);
    const onReportOK = () => {
        setOpenReport(false);
        Report('comment', props._id, reason);
    };
    const onReportChange = (event, value) => {
        if (value) {
            setReason(value.code);
            setReportOkDisabled(false);
        } else {
            setReason('');
            setReportOkDisabled(true);
        }
    };

    const onClickAvatar = () => {
        addNavigation(createProfileProp(props.user));
    };

    const createMenuButtons = (text, onClick, ariaLabel) => {
        return (
            <Button
                size='small'
                disableRipple
                variant='text'
                aria-label={ariaLabel}
                onClick={onClick}
                sx={{
                    justifyContent: 'flex-start',
                    width: 'fit-content',
                    minWidth: 0,
                    fontSize: '0.7rem',
                    p: '1px 4px',
                    textTransform: 'none',
                    color: 'primary.dark',
                }}>
                {text}
            </Button>
        );
    };

    // A comment still being text-checked: its author sees loading dots, everyone else sees nothing (like a post).
    if (props.processing && !isOwnComment) return null;

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return (
        <>
            <ListItem disablePadding alignItems='flex-start' sx={{ p: 0.5 }}>
                {/*===== avatar =====*/}
                <Box sx={{ mr: 2 }}>
                    <UserAvatar variant='comment' user={props.user} config={props.config} onClick={onClickAvatar} />
                </Box>
                <Box sx={{ alignSelf: 'flex-start', width: 'fit-content' }}>
                    <Stack direction='row' justifyItems='flex-start' alignItems='center' gap={0}>
                        <Typography variant='body2' noWrap color='primary.main'>
                            {props.user?.username}
                        </Typography>
                        <UserBadges badges={props.user?.badges} />
                        <Box component='div' sx={{ ml: 0.5, fontSize: '10px' }}>
                            {!props.deleted && (
                                <Tooltip title={time.localTimeString(props.date)}>
                                    <Box component='span'>{time.relativeTimeString(props.date, tz)}</Box>
                                </Tooltip>
                            )}
                        </Box>
                        <Stack
                            direction='row'
                            justifyItems='flex-start'
                            alignItems='center'
                            gap={0}
                            sx={{ visibility: props.deleted ? 'hidden' : 'visible', ml: 0.5 }}>
                            {!props.removed && !props.processing && (
                                <Stack direction='row' alignItems='center' gap={0} sx={{ mr: 0.5 }}>
                                    <Tooltip title={props.liked ? 'unlike' : 'like'}>
                                        <IconButton
                                            aria-label={props.liked ? 'unlike comment' : 'like comment'}
                                            size='small'
                                            disableRipple
                                            onClick={() => {
                                                setPop(props.liked ? 'unlike' : 'like');
                                                props.onToggleLike();
                                            }}
                                            sx={{ p: '1px', color: props.liked ? 'primary.main' : 'primary.dark' }}>
                                            {props.liked ? (
                                                <FavoriteIcon
                                                    onAnimationEnd={() => setPop(null)}
                                                    sx={{
                                                        fontSize: '14px',
                                                        '@keyframes heartPop': {
                                                            '0%': { transform: 'scale(1)' },
                                                            '40%': { transform: 'scale(1.35)' },
                                                            '100%': { transform: 'scale(1)' },
                                                        },
                                                        ...(pop === 'like' && { animation: 'heartPop 0.35s ease-out' }),
                                                    }}
                                                />
                                            ) : (
                                                <FavoriteBorderIcon
                                                    onAnimationEnd={() => setPop(null)}
                                                    sx={{
                                                        fontSize: '14px',
                                                        '@keyframes heartShrink': {
                                                            '0%': { transform: 'scale(1)' },
                                                            '40%': { transform: 'scale(0.7)' },
                                                            '100%': { transform: 'scale(1)' },
                                                        },
                                                        ...(pop === 'unlike' && {
                                                            animation: 'heartShrink 0.35s ease-out',
                                                        }),
                                                    }}
                                                />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                    <Typography sx={{ p: '1px', fontSize: '10px', color: 'primary.dark' }}>
                                        {number.formatNumber(props.likeCount ?? 0, 1)}
                                    </Typography>
                                </Stack>
                            )}
                            {props.parentCommentId === null &&
                                !props.processing &&
                                createMenuButtons('Reply', props.onClickReply, 'reply comment')}
                            {isOwnComment &&
                                !props.processing &&
                                createMenuButtons('Delete', props.onClickDelete, 'delete comment')}
                            {!isOwnComment && (
                                <>
                                    <IconButton
                                        size='small'
                                        onClick={onClickMore}
                                        aria-label='comment options'
                                        sx={{ p: '1px', color: 'primary.dark' }}>
                                        <Tooltip title='options'>
                                            <MoreHorizIcon sx={{ fontSize: '16px' }} />
                                        </Tooltip>
                                    </IconButton>
                                    <Menu
                                        anchorEl={menuAnchor}
                                        open={Boolean(menuAnchor)}
                                        onClose={onCloseMenu}
                                        slotProps={{ paper: { elevation: 0, variant: 'outlined' } }}>
                                        <MenuItem onClick={onClickReport} sx={{ fontSize: '14px' }}>
                                            Report
                                        </MenuItem>
                                    </Menu>
                                </>
                            )}
                        </Stack>
                    </Stack>
                    {props.processing ? (
                        <DotLoading size={5} sx={{ justifyContent: 'flex-start', mt: 0.5 }} />
                    ) : props.removed ? (
                        <Chip label='censored' size='small' color='error' variant='filled' sx={{ mt: 0.5 }} />
                    ) : props.deleted ? (
                        <Chip label='deleted' size='small' color='default' variant='filled' sx={{ mt: 0.5 }} />
                    ) : (
                        <Typography color='secondary.main' variant='body2' sx={{ wordBreak: 'break-word' }}>
                            {props.content}
                        </Typography>
                    )}
                </Box>
            </ListItem>
            <ReportDialog
                type='comment'
                open={openReport}
                title='Report Comment'
                desc='What would you like to report to us?'
                onClose={onReportClose}
                onOK={onReportOK}
                onCancel={onReportCancel}
                okDisabled={reportOkDisabled}
                onChange={onReportChange}
            />
        </>
    );
}

export { CommentListItem };
